import { Box, Flex, Text } from 'uikit'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useCallback, useState } from 'react'
import { PREDICTION_TOOLTIP_DISMISS_KEY } from 'constants/index'
import { useGetCurrentRoundCloseTimestamp } from 'state/predictions/hooks'
import { PredictionSupportedSymbol } from 'state/types'
import styled, { keyframes } from 'styled-components'
import { useConfig } from '../context/ConfigProvider'
import { formatRoundTime } from '../helpers'
import useCountdown from '../hooks/useCountdown'
import LabelPrice from './LabelPrice'
import usePollOraclePrice from '../hooks/usePollOraclePrice'
import { getTokenUrl } from 'utils/getTokenUrl'
import { useResponsive } from 'hooks/useResponse'

const Token = styled(Box)`
  margin-top: -28px;
  position: absolute;
  top: 50%;
  z-index: 30;

  & > svg {
    height: 48px;
    width: 48px;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    margin-top: -24px;
    top: -5px;
  }
`

const ImgBlock = styled.img`
  width: 99.44px;
  height: 83px;
  @media (max-width: 768px) {
    width: 66.81px;
    height: 55.77px;
    max-width: fit-content;
  }
`

const Title = styled(Text)`
  font-size: 16px;
  line-height: 21px;

  ${({ theme }) => theme.mediaQueries.lg} {
    font-size: 16px;
    line-height: 22px;
  }
`

const ClosingTitle = styled(Text)`
  font-size: 9px;
  line-height: 21px;

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 16px;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    font-size: 16px;
    line-height: 22px;
  }
`

const Interval = styled.div`
  font-size: 12px;
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.textRemark};
  ${({ theme }) => theme.mediaQueries.lg} {
    background: #14d89d;
    width: 32px;
    height: 32px;
    border-radius: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-size: 12px;
    font-weight: 700;
    margin-left: 10px;
    margin-top: 0px;
  }
`

const tooltipAnimation = keyframes`
  0%{
    opacity:0;
  }
  20%{
    opacity:1;
  }
  30%{
    transform: translateX(5px);
  }
  40%{
    transform: translateX(0px);
  }
  50%{
    transform: translateX(5px);
  }
  60%{
    transform: translateX(5px);
  }
  100%{
    opacity:1;
  }
`

export const Tooltip = styled.div`
  position: absolute;
  top: -5px;
  left: 55px;
  border-radius: 16px;
  padding: 16px;
  background: ${({ theme }) => theme.tooltip.background};
  box-shadow: ${({ theme }) => theme.tooltip.boxShadow};
  white-space: nowrap;
  opacity: 0;
  z-index: 100;
  animation: ${tooltipAnimation} 3s forwards ease-in-out;
  ${Text},svg {
    color: ${({ theme }) => theme.tooltip.text};
  }
  ${({ theme }) => theme.mediaQueries.md} {
    top: -10px;
    left: 81px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 21px;
    left: -6px;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    background: ${({ theme }) => theme.tooltip.background};
    transform: rotate(45deg);
  }
`

const Label = styled(Flex)<{ dir: 'left' | 'right'; backgroundOpacity?: boolean }>`
  position: relative;
  z-index: 1;
  background-color: ${({ theme }) => theme.card.background};
  box-shadow: ${({ theme }) => theme.shadows.level1};
  align-items: flex-start;
  flex-direction: column;
  overflow: initial;
  padding: 11px 12px;
  border-radius: 12px 0 0 12px;
  height: 56px;
  ${({ theme }) => theme.mediaQueries.lg} {
    height: 40px;
    background-color: ${({ theme, backgroundOpacity }) => (backgroundOpacity ? 'transparent' : theme.card.background)};
    align-items: center;
    border-radius: ${({ theme }) => theme.radii.card};
    flex-direction: row;
    padding: ${({ dir }) => (dir === 'right' ? '4px 5px 4px 40px' : '8px 8px 8px 40px')};
    border-radius: 20px;
    transition: 0.3s background-color ease-in-out;
    will-change: background-color;
  }
`

const TokenContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const TokenBg = styled.div`
  width: 57px;
  height: 57px;
  border-radius: 30px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 1;
  top: -15px;
  left: -23px;
`

const TokenImg = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 24px;
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`

const AnotherTokenImg = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 24px;
  filter: grayscale(1);
`

const TokenPlane = styled.div`
  height: 69px;
  width: 269px;
  background: #ffffff;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
  @media (max-width: 768px) {
    width: auto;
    height: 56px;
    padding: 12px 10px;
    border-radius: 0 12px 12px 0;
    margin-left: 0px;
  }
`

const ImgSwap = styled.img<{ sec?: boolean }>`
  width: 24px;
  height: 24px;
  transform: rotate(${({ sec }) => (sec ? '180deg' : '0')});
  @media (max-width: 768px) {
    margin-left: 4px;
    margin-right: 4px;
  }
`

const ImgSwapIcon = require('assets/images/prediction/swap.svg').default
const ImgSwapMobileIcon = require('assets/images/prediction/swap-green.svg').default

export const PricePairLabel: React.FC = () => {
  const { token } = useConfig()
  // const router = useRouter()
  const history = useHistory()
  const location: any = useLocation()
  const { isMobile } = useResponsive()
  const { price } = usePollOraclePrice()
  const isETH = token.symbol.toUpperCase() === 'ETH'

  const [dismissTooltip, setDismissTooltip] = useState(() => {
    if (localStorage?.getItem(PREDICTION_TOOLTIP_DISMISS_KEY)) return true
    return false
  })

  const onDismissTooltip = useCallback(() => {
    localStorage?.setItem(PREDICTION_TOOLTIP_DISMISS_KEY, '1')
    setDismissTooltip(true)
  }, [])

  const onTokenSwitch = useCallback(() => {
    const state: any = {}
    if (location.state?.token === PredictionSupportedSymbol.ETH) {
      state.token = PredictionSupportedSymbol.BTC
    } else if (location.state?.token === undefined && token.symbol === PredictionSupportedSymbol.ETH) {
      state.token = PredictionSupportedSymbol.BTC
    } else if (location.state?.token === undefined && token.symbol === PredictionSupportedSymbol.BTC) {
      state.token = PredictionSupportedSymbol.ETH
    } else if (token.symbol === undefined && location.state?.token === undefined) {
      state.token = PredictionSupportedSymbol.BTC
    } else {
      state.token = PredictionSupportedSymbol.ETH
    }
    if (!dismissTooltip) onDismissTooltip()
    const toLocation = { ...location, state }
    history.replace(toLocation)
  }, [token, dismissTooltip, onDismissTooltip, history, location])

  if (isMobile) {
    return (
      <TokenPlane
        onClick={() => {
          onTokenSwitch()
        }}
      >
        <Flex alignItems="center">
          <TokenImg src={getTokenUrl(token.symbol)} />
          <ImgSwap src={ImgSwapMobileIcon} />
        </Flex>
        <div>
          <Text bold fontSize="14px">{`${token.symbol.toUpperCase()}USD`}</Text>
          <LabelPrice price={price} />
        </div>
      </TokenPlane>
    )
  }

  return (
    <>
      <Box position="relative" display="inline-block">
        <TokenContainer onClick={() => onTokenSwitch()}>
          <TokenBg>
            <TokenImg src={getTokenUrl(token.symbol)} />
          </TokenBg>
          <TokenPlane>
            <Flex alignItems="center" flexDirection="column" ml="7px">
              <Flex>
                <div style={{ width: '24px' }} />
                <Text bold ml="12px">{`${token.symbol.toUpperCase()}/USD`}</Text>
              </Flex>
              <Flex style={{ width: '100%' }}>
                <AnotherTokenImg src={getTokenUrl(isETH ? 'btck' : 'eth')} />
                <Text color="borderDark" fontSize="14px" bold ml="12px">
                  {isETH ? 'BTCK/USDT' : 'ETH/USD'}
                </Text>
              </Flex>
            </Flex>
            <Flex alignItems="center" mr="20px">
              <LabelPrice price={price} />
              <ImgSwap src={ImgSwapIcon} />
            </Flex>
          </TokenPlane>
        </TokenContainer>
      </Box>
    </>
  )
}

interface TimerLabelProps {
  interval: string
  unit: 'm' | 'h' | 'd'
}

export const TimerLabel: React.FC<React.PropsWithChildren<TimerLabelProps>> = ({ interval, unit }) => {
  const currentRoundCloseTimestamp = useGetCurrentRoundCloseTimestamp()
  const { secondsRemaining } = useCountdown(currentRoundCloseTimestamp)
  const { isMobile } = useResponsive()

  const countdown = formatRoundTime(secondsRemaining)
  const { t } = useTranslation()

  if (!currentRoundCloseTimestamp) {
    return null
  }

  return (
    <Box pr={isMobile ? '0px' : '24px'} position="relative">
      <Token left={isMobile ? 50 : -50}>
        <ImgBlock src={require('assets/images/prediction/clock.png').default} />
      </Token>
      <Label dir="right">
        {secondsRemaining !== 0 ? (
          <Title bold color="text">
            {countdown}
          </Title>
        ) : (
          <ClosingTitle bold color="text">
            {t('PREDICTION_30')}
          </ClosingTitle>
        )}
        <Interval>{`${interval}${t(unit)}`}</Interval>
      </Label>
    </Box>
  )
}
